import { useState, useEffect } from 'react';
import { Dialog, Stack, Button, TextField, useMediaQuery } from '@mui/material';
import { useTheme } from '@material-ui/core/styles';
import AutoCompleteSelector from '../AutoCompleteSelector';
import { useQuery, useMutation } from 'react-query';
import { getSpecializations } from 'src/services/specializations.service';
import { createPolicy, updatePolicy } from 'src/services/policies.service';
import { useRecoilState } from 'recoil';
import { snackbarAtom } from 'src/atoms/snackbar';
import PulseLoader from '../loaders/PulseLoader';
import { queryClient } from 'src';
import moment from 'moment';

const PolicyDialog = ({ open, onClose, state = 'create', policy }) => {
  const [, setSnackBar] = useRecoilState(snackbarAtom);
  const theme = useTheme();
  const {
    isLoading: specIsLoading,
    isError: specIsError,
    data: specializations,
    error: specError
  } = useQuery('specializations', getSpecializations);

  const {
    mutate: addPolicy,
    isLoading: addPolicyIsLoading,
    isError: addPolicyIsError,
    error: addPolicyError
  } = useMutation('addPolicy', createPolicy);

  const {
    mutate: editPolicy,
    isLoading: editPolicyIsLoading,
    isError: editPolicyIsError,
    error: editPolicyError
  } = useMutation('editPolicy', updatePolicy);

  const [specs, setSpecs] = useState([]);
  const [policyName, setPolicyName] = useState('');
  const [policyDuration, setPolicyDuration] = useState();
  const [policyAmount, setPolicyAmount] = useState();
  const [policyMaxRequets, setPolicyMaxRequets] = useState();
  const [policyStartDate, setPolicyStartDate] = useState();

  const [defaultValue, setDefaultValue] = useState([]);
  useEffect(() => {
    if (specIsError || addPolicyIsError || editPolicyIsError) {
      onDialogClose();
      setSnackBar({
        open: true,
        severity: 'error',
        message: (specError || addPolicyError || editPolicyError).message
      });
    }

    if (policy) {
      setPolicyName(policy.name);
      setPolicyDuration(policy.duration);
      setPolicyAmount(policy.amount);
      setSpecs(
        specializations?.data.filter((spec) =>
          policy.specializations
            .split(', ')
            .find((item) => item === spec.specialization_description)
        )
      );
      setDefaultValue(
        specializations?.data && policy.specializations
          ? specializations?.data
              .filter((spec) =>
                policy.specializations
                  .split(', ')
                  .find((item) => item === spec.specialization_description)
              )
              .map((spec) => ({
                id: spec.id,
                title: spec.specialization_description
              }))
          : []
      );

      setPolicyMaxRequets(policy.max_requests);
      setPolicyStartDate(
        policy.start_date ? moment(new Date(policy.start_date)).format('yyyy-MM-DD') : null
      );
    }

    return () => {
      setPolicyName('');
      setPolicyDuration();
      setPolicyAmount();
      setSpecs([]);
    };
  }, [specIsError, policy, specializations, addPolicyIsError, editPolicyIsError]);

  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const onAddPolicy = () => {
    addPolicy(
      {
        name: policyName,
        duration: Number(policyDuration),
        amount: Number(policyAmount),
        specializations: specs,
        max_requests: policyMaxRequets || undefined,
        start_date: policyStartDate ? new Date(policyStartDate) : undefined
      },
      {
        onSuccess: () => {
          onDialogClose();
          queryClient.invalidateQueries('policies');
        }
      }
    );
  };

  const onEditPolicy = () => {
    editPolicy(
      {
        id: policy.id,
        name: policyName,
        duration: Number(policyDuration),
        amount: Number(policyAmount),
        specializations: specs,
        max_requests: policyMaxRequets || undefined,
        start_date: policyStartDate ? new Date(policyStartDate) : undefined
      },
      {
        onSuccess: () => {
          onDialogClose();
          queryClient.invalidateQueries('policies');
        }
      }
    );
  };

  const onDialogClose = () => {
    onClose();
    setPolicyName('');
    setPolicyDuration();
    setPolicyAmount();
    setSpecs([]);
    setDefaultValue([]);
    setPolicyMaxRequets();
    setPolicyStartDate();
  };

  const handleKeypress = (e) => {
    //it triggers by pressing the enter key
    if (e.keyCode === 13) {
      state === 'create' ? onAddPolicy() : onEditPolicy();
    }
  };

  return (
    <Dialog open={open} onClose={() => onDialogClose()} style={{}} onKeyPress={handleKeypress}>
      <Stack
        alignItems={'center'}
        justifyContent={'space-evenly'}
        style={{
          minHeight: '30vh',
          gap: '2rem',
          width: isLargeScreen ? '30vw' : isSmallScreen ? '80vw' : '50vw',
          padding: '2rem'
        }}
      >
        <h1 align="center" style={{ marginBottom: '0.5rem' }}>
          {state === 'create' ? 'Add ' : 'Edit '} Policy
        </h1>
        {specIsLoading ? (
          <PulseLoader />
        ) : (
          <Stack width={'100%'} gap={2}>
            <TextField
              fullWidth
              id="policy-name"
              label="Policy Name"
              variant="outlined"
              size="small"
              onChange={(e) => setPolicyName(e.target.value)}
              value={policyName}
            />
            <TextField
              fullWidth
              id="policy-duration"
              label="Duration (months)"
              variant="outlined"
              size="small"
              type="number"
              onWheel={(e) => e.target.blur()}
              onChange={(e) => setPolicyDuration(e.target.value)}
              value={policyDuration}
            />
            <TextField
              fullWidth
              id="policy-amount"
              label="Amount (USD)"
              onWheel={(e) => e.target.blur()}
              variant="outlined"
              size="small"
              type="number"
              onChange={(e) => setPolicyAmount(e.target.value)}
              value={policyAmount}
            />
            <div>
              <AutoCompleteSelector
                data={specializations.data.map((spec) => ({
                  id: spec.id,
                  title: spec.specialization_description
                }))}
                defaultValue={defaultValue}
                label={'Specializations'}
                onChange={(specs) =>
                  setSpecs(
                    specs.map((spec) => {
                      return {
                        id: spec.id,
                        specialization_description: spec.title
                      };
                    })
                  )
                }
              />
            </div>
            <TextField
              fullWidth
              id="policy-max-req"
              label="Max. Requests (Optional)"
              variant="outlined"
              size="small"
              type="number"
              onWheel={(e) => e.target.blur()}
              onChange={(e) => setPolicyMaxRequets(Number(e.target.value))}
              value={policyMaxRequets || NaN}
            />
            <TextField
              fullWidth
              id="policy-start-date"
              label="Start Date (Optional)"
              variant="outlined"
              size="small"
              type="date"
              onChange={(e) =>
                setPolicyStartDate(moment(new Date(e.target.value)).format('yyyy-MM-DD'))
              }
              value={policyStartDate}
              InputLabelProps={{
                shrink: true
              }}
            />
          </Stack>
        )}
        <Stack direction={'row'} width="100%" gap={2}>
          <Button
            variant="contained"
            color="incorta"
            fullWidth
            size="large"
            disabled={
              specIsLoading ||
              addPolicyIsLoading ||
              editPolicyIsLoading ||
              !policyName ||
              !policyDuration ||
              !policyAmount ||
              !specs.length
            }
            onClick={state === 'create' ? onAddPolicy : onEditPolicy}
          >
            {addPolicyIsLoading || editPolicyIsLoading ? (
              <PulseLoader size={10} color="white" />
            ) : (
              'Save'
            )}
          </Button>
          <Button
            variant="contained"
            color="error"
            fullWidth
            size="large"
            onClick={onDialogClose}
            disabled={specIsLoading}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default PolicyDialog;
